import { useState, useEffect, useCallback, useRef } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { AuthGuard, AuthProvider, useAuth, SearchHit, lockLandscapeOrientation } from '@nyos/shared'
import { ScopeProvider, useScope } from '@nyos/core-framework'
import PostLoginScopeOverlay from './components/PostLoginScopeOverlay'
import YardMap, { MapFocusTarget } from './components/YardMap'
import ScenarioControls from './components/ScenarioControls'
import TopNav from './components/TopNav'
import ScreenShell from './components/ScreenShell'
import DrawerContainer from './components/DrawerContainer'
import GlobalHeader, { GlobalHeaderRef } from './components/GlobalHeader'
import TurnoverModal from './components/TurnoverModal'
import SwitchJobsDrawer from './components/SwitchJobsDrawer'
import SwitchJobBuilderPage from './features/execution/pages/SwitchJobBuilderPage'
import InsightsIndexPage from './features/execution/pages/InsightsIndexPage'
import SwitchJobStoryPage from './features/execution/pages/SwitchJobStoryPage'
import SwitchTracePage from './features/execution/pages/SwitchTracePage'
import TurninsDrawer from './components/TurninsDrawer'
import InboundInterchange from './features/b2b/InboundInterchange'
import SpatialIntegrityHub from './components/SpatialIntegrityHub'
import ComingSoonPage from './components/ComingSoonPage'
import Toast from './components/Toast'
import { useTheme } from './contexts/ThemeContext'
import { YardProvider } from './contexts/YardContext'
import { AlertProvider } from './contexts/AlertContext'
import { ToastProvider, useToast } from './contexts/ToastContext'
import { ScenarioProvider } from './contexts/ScenarioContext'
import { WebSocketProvider } from './contexts/WebSocketContext'
import './App.css'

function ToastContainer() {
  const { toasts, removeToast } = useToast()

  return (
    <>
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onRemove={removeToast} />
      ))}
    </>
  )
}

function getWebSocketUrl(): string {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080'
  const base = new URL(apiUrl)
  const protocol = base.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${base.host}/ws/railcar-movements`
}

function App() {
  useEffect(() => {
    // Plan-30: pin landscape for the installed demo PWA. The manifest already
    // declares orientation — this covers installs whose manifest was captured
    // before that field and OEM builds that ignore it. Runs before auth so the
    // login screen is locked too.
    void lockLandscapeOrientation()
  }, [])

  return (
    <AuthProvider>
      <AuthGuard requiredRole={['ROLE_YARDMASTER', 'ROLE_SUPERADMIN']}>
        <AppWithScope />
      </AuthGuard>
    </AuthProvider>
  )
}

function AppWithScope() {
  const { user } = useAuth()
  const wsUrl = getWebSocketUrl()

  const initialScopes = user?.availableFacilities?.map((f: any) => ({
    tenantId: f.tenantId,
    operatingGroupId: f.operatingGroupId,
    operatingEntityId: f.operatingEntityId,
    operatingEntityName: f.operatingEntityName,
    facilityId: f.facilityId,
    subZoneId: f.subZoneId,
    name: f.facilityName || f.name
  })) || []

  return (
    <ScopeProvider initialScopes={initialScopes}>
      <AlertProvider>
        <ToastProvider>
          <YardProvider>
            <ScenarioProvider>
              <WebSocketProvider url={wsUrl}>
                <ScopeGate />
              </WebSocketProvider>
            </ScenarioProvider>
          </YardProvider>
        </ToastProvider>
      </AlertProvider>
    </ScopeProvider>
  )
}

function ScopeGate() {
  const { activeScope, availableScopes } = useScope()
  if (availableScopes.length > 1 && !activeScope) {
    return <PostLoginScopeOverlay />
  }
  return <AppContent />
}

function AppContent() {
  const { theme } = useTheme()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerType, setDrawerType] = useState<'inspector' | 'comms' | null>(null)
  const [selectedTrack, setSelectedTrack] = useState<string | null>(null)
  const [turnoverOpen, setTurnoverOpen] = useState(false)
  const [switchJobsOpen, setSwitchJobsOpen] = useState(false)
  const [turninsOpen, setTurninsOpen] = useState(false)
  const [mapFocus, setMapFocus] = useState<MapFocusTarget | null>(null)
  const [jobFocusId, setJobFocusId] = useState<string | null>(null)
  const globalHeaderRef = useRef<GlobalHeaderRef>(null)


  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDrawerOpen(false)
        setTurnoverOpen(false)
        setSwitchJobsOpen(false)
        setTurninsOpen(false)
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [])

  const openInspectorDrawer = useCallback((trackId: string) => {
    setSelectedTrack(trackId)
    setDrawerType('inspector')
    setDrawerOpen(true)
  }, [setSelectedTrack, setDrawerType, setDrawerOpen])

  const openCommsDrawer = useCallback(() => {
    setDrawerType('comms')
    setDrawerOpen(true)
  }, [setDrawerType, setDrawerOpen])

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false)
    setDrawerType(null)
    setSelectedTrack(null)
  }, [setDrawerOpen, setDrawerType, setSelectedTrack])

  const openTurnover = useCallback(() => {
    setTurnoverOpen(true)
  }, [setTurnoverOpen])

  const closeTurnover = useCallback(() => {
    setTurnoverOpen(false)
  }, [setTurnoverOpen])

  const handleTurnoverAcknowledge = useCallback(() => {
    console.log('Turnover acknowledged')
    setTurnoverOpen(false)
  }, [setTurnoverOpen])

  const openSwitchJobs = useCallback(() => {
    setSwitchJobsOpen(true)
  }, [setSwitchJobsOpen])

  const closeSwitchJobs = useCallback(() => {
    setSwitchJobsOpen(false)
  }, [setSwitchJobsOpen])

  const openTurnins = useCallback(() => {
    setTurninsOpen(true)
  }, [setTurninsOpen])

  // Header search landing — one result = one action.
  //   car   → '/' + map focus on its track+offset, inspector opens
  //   track → '/' + map focus on the track bbox
  //   job   → Job Queue drawer with the row highlighted
  const handleSearchSelect = useCallback((hit: SearchHit) => {
    if (hit.kind === 'job') {
      setJobFocusId(hit.id)
      setSwitchJobsOpen(true)
      return
    }
    const trackId = hit.kind === 'car' ? hit.trackId : hit.id
    if (!trackId) return // car with no kinematics — nothing to locate
    setMapFocus({
      kind: hit.kind === 'car' ? 'car' : 'track',
      trackId,
      linearOffset: hit.kind === 'car' ? hit.linearOffset : undefined,
      // Chip shows the entity name — never the UUID.
      label: hit.kind === 'car'
        ? `${hit.reportingMark}${hit.trackName ? ` · ${hit.trackName}` : ''}`
        : hit.name,
      requestId: Date.now(),
    })
    navigate('/')
    if (hit.kind === 'car') {
      openInspectorDrawer(trackId)
    }
  }, [navigate, openInspectorDrawer])

  const closeTurnins = useCallback(() => {
    setTurninsOpen(false)
  }, [setTurninsOpen])

  return (
    <div className="h-dvh flex flex-col bg-main">
      <GlobalHeader
        ref={globalHeaderRef}
        onNotificationsClick={openCommsDrawer}
        onHealthClick={() => console.log('Health clicked')}
        onSearchSelect={handleSearchSelect}
      />
      <TopNav
        onOpenTurnover={openTurnover}
        onOpenSwitchJobs={openSwitchJobs}
        onOpenTurnins={openTurnins}
      />
      <div className="flex-1 min-h-0 overflow-hidden relative">
        <Routes>
          <Route path="/" element={
            <ScreenShell
              title="Yard Board — Live"
              subtitle="Where everything stands — every car, every track, live — click a track to act"
              actions={<ScenarioControls workflowType="SIMULATION" />}
            >
              <YardMap
                key="yardmap"
                onTrackClick={openInspectorDrawer}
                activeSweepAudits={[]}
                activeTheme={theme}
                focusTarget={mapFocus}
                onFocusClear={() => setMapFocus(null)}
              />
            </ScreenShell>
          } />
          <Route path="/dashboard/inbound-interchange" element={<InboundInterchange key="inbound-interchange" />} />
          <Route path="/admin/spatial-integrity" element={
            user?.role === 'ROLE_SUPERADMIN' || user?.role === 'ROLE_ADMIN'
              ? <SpatialIntegrityHub key="spatial-integrity" />
              : <Navigate to="/" replace />
          } />
          <Route path="/execution/switch-jobs" element={<SwitchJobBuilderPage key="switch-job-builder" />} />
          <Route path="/insights" element={<InsightsIndexPage key="insights" />} />
          <Route path="/execution/switch-jobs/:id/story" element={<SwitchJobStoryPage key="switch-job-story" />} />
          <Route path="/admin/switch-trace" element={<Navigate to="/insights" replace />} />
          <Route path="/admin/switch-trace/:id" element={<SwitchTracePage key="switch-trace" />} />
          {/* Roadmap stubs — shared ComingSoonPage, no API/state. */}
          <Route path="/operations/sweeps" element={<ComingSoonPage title="Sweeps" blurb="Plan the day's pulls and spots once — dispatch a coordinated sweep, fewer crew-hours per car." />} />
          <Route path="/operations/inventory" element={<ComingSoonPage title="Inventory" blurb="Every car on the property, one ledger — dwell clocks, status flags, and discrepancies caught before they cost you." />} />
          <Route path="/interchange/outbound" element={<ComingSoonPage title="Outbound — Class I Handoff" blurb="Build compliant outbound trains in minutes — makeup, placement rules, weight and length checked before the Class I arrives." />} />
          <Route path="/safety/blue-flag" element={<ComingSoonPage title="Blue Flag Protection" blurb="Protection enforced by the system, not by paperwork — crew safety and your compliance record." />} />
          <Route path="/safety/exception-ledger" element={<ComingSoonPage title="Exception Ledger" blurb="Every accepted caveat on record — who, what, when — the audit trail your insurers and the FRA ask for." />} />
          <Route path="/insights/yard-dashboard" element={<ComingSoonPage title="Yard Dashboard" blurb="The yard's vital signs at a glance — occupancy, dwell, alerts — every number tagged with how much you can trust it." />} />
          <Route path="/insights/carbon-esg" element={<ComingSoonPage title="Carbon & ESG" blurb="Rail versus truck in hard numbers — CO₂ avoided per shipper, quarter and year to date, audit-ready for their sustainability reports." />} />
          <Route path="/revenue/demurrage" element={<ComingSoonPage title="Demurrage & Dwell" blurb="Dwell you can bill because you can prove it — sourced from verified events, not estimates." />} />
          <Route path="/revenue/billing" element={<ComingSoonPage title="Billing & Invoices" blurb="Verified yard events become invoices automatically — every charge traceable to something that actually happened." />} />
          <Route path="/portal/shipper" element={<ComingSoonPage title="Self-Service" blurb="Your customers order cars, request spots, and track shipments themselves — fewer phone calls, more trust." />} />
          <Route path="/portal/carbon" element={<ComingSoonPage title="Carbon Corner" blurb="Your customers' own emissions dashboard — quarterly CO₂ savings and certificates their sustainability teams can file." />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <DrawerContainer
        isOpen={drawerOpen}
        type={drawerType}
        selectedTrack={selectedTrack}
        onClose={closeDrawer}
        targetRef={drawerType === 'comms' ? globalHeaderRef.current?.notificationIconRef : undefined}
      />
      <TurnoverModal
        isOpen={turnoverOpen}
        onClose={closeTurnover}
        onAcknowledge={handleTurnoverAcknowledge}
      />
      <SwitchJobsDrawer
        isOpen={switchJobsOpen}
        onClose={closeSwitchJobs}
        focusId={jobFocusId}
      />
      <TurninsDrawer
        isOpen={turninsOpen}
        onClose={closeTurnins}
      />
      <ToastContainer />
    </div>
  )
}

export default App
